// src/services/reporting/workloadReports.service.ts
import { workloadService } from '../workload.service';
import { addressService } from '../address.service';
import { Workload } from '../../types/workload.types';
import { AddressStatus } from '../../types/address.types';

export interface WorkloadSummaryReport {
  projectId: string;
  generatedAt: Date;
  totalWorkloads: number;
  totalAddresses: number;
  completedAddresses: number;
  overallCompletionRate: number;
  byStatus: Record<string, { count: number; addresses: number; completed: number }>;
  byCanvasser: Array<{
    userId: string;
    userName: string;
    workloadsAssigned: number;
    addressesCompleted: number;
    completionRate: number;
  }>;
  recentActivity: Array<{
    date: string;
    completed: number;
    visited: number;
  }>;
}

export interface WorkloadDetailReport {
  workload: Workload;
  addressBreakdown: Record<AddressStatus, number>;
  dailyProgress: Array<{
    date: string;
    completed: number;
    visited: number;
    notHome: number;
    refused: number;
  }>;
  canvasserPerformance: Array<{
    userId: string;
    userName: string;
    addressesVisited: number;
    addressesCompleted: number;
    avgTimePerAddress?: number;
  }>;
  geographicDistribution: {
    centerLat: number;
    centerLng: number;
    bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    };
    heatmapData: Array<{
      lat: number;
      lng: number;
      weight: number;
    }>;
  };
}

export interface CanvasserReport {
  userId: string;
  userName: string;
  dateRange: { start: Date; end: Date };
  totalVisits: number;
  completedAddresses: number;
  notHomeVisits: number;
  refusedVisits: number;
  completionRate: number;
  avgVisitsPerDay: number;
  surveysCompleted: number;
  dailyActivity: Array<{
    date: string;
    visits: number;
    completed: number;
  }>;
  workloadsWorked: Array<{
    workloadId: string;
    workloadName: string;
    addressesVisited: number;
  }>;
}

class WorkloadReportsService {
  async generateProjectSummary(projectId: string): Promise<WorkloadSummaryReport> {
    // Get all workloads
    const workloads = await workloadService.getProjectWorkloads(projectId);

    // Calculate totals
    let totalAddresses = 0;
    let completedAddresses = 0;
    const byStatus: Record<string, { count: number; addresses: number; completed: number }> = {};
    const canvasserMap = new Map<string, {
      userName: string;
      workloads: Set<string>;
      completed: number;
      total: number;
    }>();

    for (const workload of workloads) {
      totalAddresses += workload.progress.totalAddresses;
      completedAddresses += workload.progress.completed;

      // By status
      if (!byStatus[workload.status]) {
        byStatus[workload.status] = { count: 0, addresses: 0, completed: 0 };
      }
      byStatus[workload.status].count++;
      byStatus[workload.status].addresses += workload.progress.totalAddresses;
      byStatus[workload.status].completed += workload.progress.completed;

      // By canvasser
      if (workload.assignedTo) {
        workload.assignedTo.forEach((userId, index) => {
          const userName = workload.assignedToNames?.[index] || 'Unknown';
          if (!canvasserMap.has(userId)) {
            canvasserMap.set(userId, {
              userName,
              workloads: new Set(),
              completed: 0,
              total: 0,
            });
          }
          const data = canvasserMap.get(userId)!;
          data.workloads.add(workload.id);
          data.completed += workload.progress.completed / (workload.assignedTo?.length || 1);
          data.total += workload.progress.totalAddresses / (workload.assignedTo?.length || 1);
        });
      }
    }

    const byCanvasser = Array.from(canvasserMap.entries()).map(([userId, data]) => ({
      userId,
      userName: data.userName,
      workloadsAssigned: data.workloads.size,
      addressesCompleted: Math.round(data.completed),
      completionRate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
    }));

    // Sort by addresses completed
    byCanvasser.sort((a, b) => b.addressesCompleted - a.addressesCompleted);

    // Recent activity (last 7 days)
    const recentActivity = this.generateDailyActivity(7);

    return {
      projectId,
      generatedAt: new Date(),
      totalWorkloads: workloads.length,
      totalAddresses,
      completedAddresses,
      overallCompletionRate: totalAddresses > 0
        ? Math.round((completedAddresses / totalAddresses) * 100)
        : 0,
      byStatus,
      byCanvasser,
      recentActivity,
    };
  }

  async generateWorkloadDetail(workloadId: string): Promise<WorkloadDetailReport | null> {
    const workload = await workloadService.get(workloadId);
    if (!workload) return null;

    const addresses = await addressService.getWorkloadAddresses(workloadId);

    // Address breakdown by status
    const addressBreakdown: Record<AddressStatus, number> = {
      pending: 0,
      visited: 0,
      not_home: 0,
      refused: 0,
      completed: 0,
      inaccessible: 0,
    };

    addresses.forEach((addr) => {
      addressBreakdown[addr.status]++;
    });

    // Daily progress
    const dailyProgress = this.generateDailyActivity(14).map((day) => ({
      date: day.date,
      completed: 0,
      visited: 0,
      notHome: 0,
      refused: 0,
    }));

    // Canvasser performance
    const canvasserPerformance: WorkloadDetailReport['canvasserPerformance'] = [];
    if (workload.assignedTo) {
      workload.assignedTo.forEach((userId, index) => {
        canvasserPerformance.push({
          userId,
          userName: workload.assignedToNames?.[index] || 'Unknown',
          addressesVisited: Math.round(addresses.filter((a) => a.status !== 'pending').length / workload.assignedTo!.length),
          addressesCompleted: Math.round(addresses.filter((a) => a.status === 'completed').length / workload.assignedTo!.length),
        });
      });
    }

    // Geographic distribution
    const validAddresses = addresses.filter((a) => a.location?.lat && a.location?.lng);
    let centerLat = 0;
    let centerLng = 0;
    let north = -90;
    let south = 90;
    let east = -180;
    let west = 180;

    if (validAddresses.length > 0) {
      validAddresses.forEach((addr) => {
        centerLat += addr.location.lat;
        centerLng += addr.location.lng;
        north = Math.max(north, addr.location.lat);
        south = Math.min(south, addr.location.lat);
        east = Math.max(east, addr.location.lng);
        west = Math.min(west, addr.location.lng);
      });
      centerLat /= validAddresses.length;
      centerLng /= validAddresses.length;
    }

    const heatmapData = validAddresses.map((addr) => ({
      lat: addr.location.lat,
      lng: addr.location.lng,
      weight: addr.status === 'completed' ? 1 : addr.status === 'pending' ? 0.3 : 0.6,
    }));

    return {
      workload,
      addressBreakdown,
      dailyProgress,
      canvasserPerformance,
      geographicDistribution: {
        centerLat,
        centerLng,
        bounds: { north, south, east, west },
        heatmapData,
      },
    };
  }

  async generateCanvasserReport(
    userId: string,
    userName: string,
    projectId: string,
    dateRange: { start: Date; end: Date }
  ): Promise<CanvasserReport> {
    const workloads = await workloadService.getProjectWorkloads(projectId);
    const assignedWorkloads = workloads.filter((w) => w.assignedTo?.includes(userId));

    let totalVisits = 0;
    let completedAddresses = 0;
    let notHomeVisits = 0;
    let refusedVisits = 0;
    let surveysCompleted = 0;
    const workloadsWorked: CanvasserReport['workloadsWorked'] = [];

    for (const workload of assignedWorkloads) {
      const addresses = await addressService.getWorkloadAddresses(workload.id);

      const visited = addresses.filter((a) => a.status !== 'pending').length;
      const completed = addresses.filter((a) => a.status === 'completed').length;
      const notHome = addresses.filter((a) => a.status === 'not_home').length;
      const refused = addresses.filter((a) => a.status === 'refused').length;
      const withSurvey = addresses.filter((a) => a.surveyResponseId).length;

      // Divide by number of assignees
      const divisor = workload.assignedTo?.length || 1;
      totalVisits += Math.round(visited / divisor);
      completedAddresses += Math.round(completed / divisor);
      notHomeVisits += Math.round(notHome / divisor);
      refusedVisits += Math.round(refused / divisor);
      surveysCompleted += Math.round(withSurvey / divisor);

      workloadsWorked.push({
        workloadId: workload.id,
        workloadName: workload.name,
        addressesVisited: Math.round(visited / divisor),
      });
    }

    const daysDiff = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
    );

    const dailyActivity = this.generateDailyActivity(daysDiff).map((day) => ({
      date: day.date,
      visits: Math.round(totalVisits / daysDiff),
      completed: Math.round(completedAddresses / daysDiff),
    }));

    return {
      userId,
      userName,
      dateRange,
      totalVisits,
      completedAddresses,
      notHomeVisits,
      refusedVisits,
      completionRate: totalVisits > 0 ? Math.round((completedAddresses / totalVisits) * 100) : 0,
      avgVisitsPerDay: daysDiff > 0 ? Math.round(totalVisits / daysDiff) : 0,
      surveysCompleted,
      dailyActivity,
      workloadsWorked,
    };
  }

  exportToCSV(data: any[], filename: string): string {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((h) => {
        const value = row[h];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value).includes(',') ? `"${value}"` : value;
      }).join(',')
    );

    return [headers.join(','), ...rows].join('\n');
  }

  private generateDailyActivity(days: number): Array<{ date: string; completed: number; visited: number }> {
    const result: Array<{ date: string; completed: number; visited: number }> = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      result.push({
        date: date.toISOString().split('T')[0],
        completed: 0,
        visited: 0,
      });
    }

    return result;
  }
}

export const workloadReportsService = new WorkloadReportsService();
